import type { SeedIsland } from './seedIslands'

/**
 * The Mandarin expansion set — the topics the other four languages carry that
 * Mandarin was missing: directions, shopping, small talk, arranging to meet,
 * things going wrong, and holding an opinion.
 *
 * Pinyin is stored as spoken, with 一 and 不 sandhi applied, matching the
 * original set. seed.test.ts checks every block's syllable count against its
 * character count, so a transcription slip here fails the build rather than
 * quietly marking a correct answer wrong for the rest of the app's life.
 */
export const MORE_ZH: SeedIsland[] = [
  {
    id: 'isl_wenlu',
    title: 'Asking directions',
    titleZh: '问路',
    useWhen: 'You need this while tired and carrying luggage. Overlearn it.',
    tags: ['transactional', 'travel'],
    hskLevel: 2,
    blocks: [
      [
        '请问，去火车站怎么走？',
        'qǐng wèn qù huǒ chē zhàn zěn me zǒu',
        'Excuse me, how do I get to the railway station?',
        '怎么走 is the fixed way to ask a route on foot.',
      ],
      ['这趟车到机场吗？', 'zhè tàng chē dào jī chǎng ma', 'Does this service go to the airport?'],
      [
        '买一张去上海的往返票。',
        'mǎi yì zhāng qù shàng hǎi de wǎng fǎn piào',
        'One return ticket to Shanghai.',
        '张 is the measure word for tickets and anything flat.',
      ],
      ['请问在几号站台？', 'qǐng wèn zài jǐ hào zhàn tái', 'Which platform is it, please?'],
      [
        '到了麻烦您叫我一声。',
        'dào le má fan nín jiào wǒ yì shēng',
        'Would you give me a shout when we arrive?',
        '叫我一声 — one call, one shout. 一声 is doing the work of "just".',
      ],
    ],
  },
  {
    id: 'isl_maidongxi',
    title: 'Shopping',
    titleZh: '买东西',
    useWhen: 'Short, frequent exchanges where hesitating costs you the queue.',
    tags: ['transactional', 'shopping'],
    hskLevel: 1,
    blocks: [
      ['请问，这个多少钱？', 'qǐng wèn zhè ge duō shao qián', 'Excuse me, how much is this?'],
      [
        '我就是随便看看。',
        'wǒ jiù shì suí biàn kàn kan',
        "I'm just having a look.",
        'The stock answer to a shop assistant. Doubling 看 softens it.',
      ],
      ['有大一点的吗？', 'yǒu dà yì diǎn de ma', 'Do you have a slightly bigger one?'],
      ['可以刷卡吗？', 'kě yǐ shuā kǎ ma', 'Can I pay by card?', '刷卡 — literally to swipe a card.'],
      ['就要这个，谢谢。', 'jiù yào zhè ge xiè xie', "I'll take this one, thanks."],
    ],
  },
  {
    id: 'isl_xianliao',
    title: 'Weather and small talk',
    titleZh: '闲聊',
    useWhen: 'The filler that keeps a silence from becoming awkward.',
    tags: ['social'],
    hskLevel: 2,
    blocks: [
      ['今天天气真好。', 'jīn tiān tiān qì zhēn hǎo', 'The weather is lovely today.'],
      ['终于暖和起来了。', 'zhōng yú nuǎn huo qǐ lai le', "It's finally warming up."],
      [
        '好久没有这么好的天气了。',
        'hǎo jiǔ méi yǒu zhè me hǎo de tiān qì le',
        "We haven't had weather this good in ages.",
      ],
      ['今天真是累坏了。', 'jīn tiān zhēn shì lèi huài le', "I'm absolutely worn out today.", '累坏了 — worn out to the point of breaking.'],
      ['这个周末来得正好。', 'zhè ge zhōu mò lái de zhèng hǎo', 'This weekend comes at just the right time.'],
    ],
  },
  {
    id: 'isl_yueshijian',
    title: 'Making plans',
    titleZh: '约时间',
    useWhen: 'The moment a conversation could become a second meeting.',
    tags: ['social'],
    hskLevel: 2,
    blocks: [
      [
        '有空一起喝杯咖啡吧？',
        'yǒu kòng yì qǐ hē bēi kā fēi ba',
        'Shall we get a coffee sometime?',
        '吧 turns a statement into a gentle suggestion. Without it this is an instruction.',
      ],
      ['你什么时候方便？', 'nǐ shén me shí hou fāng biàn', 'When suits you?'],
      ['我平时晚上都可以。', 'wǒ píng shí wǎn shang dōu kě yǐ', 'Evenings generally work for me.'],
      ['那就周四七点吧。', 'nà jiù zhōu sì qī diǎn ba', 'Thursday at seven, then.'],
      ['我再发消息告诉你地方。', 'wǒ zài fā xiāo xi gào su nǐ dì fang', "I'll message you the place."],
    ],
  },
  {
    id: 'isl_chuwenti',
    title: 'When something goes wrong',
    titleZh: '出问题的时候',
    useWhen: 'The island you will need under stress, which is when improvising fails.',
    tags: ['transactional', 'problems'],
    hskLevel: 3,
    blocks: [
      [
        '不好意思，这里好像有问题。',
        'bù hǎo yì si zhè lǐ hǎo xiàng yǒu wèn tí',
        'Excuse me, there seems to be a problem here.',
        '好像 softens the accusation — "seems" rather than "is".',
      ],
      [
        '我昨天点的东西一直没到。',
        'wǒ zuó tiān diǎn de dōng xi yì zhí méi dào',
        "What I ordered yesterday still hasn't arrived.",
      ],
      ['您能帮我一下吗？', 'nín néng bāng wǒ yí xià ma', 'Could you help me for a moment?'],
      [
        '我没太听懂，能再说一遍吗？',
        'wǒ méi tài tīng dǒng néng zài shuō yí biàn ma',
        "I didn't quite catch that — could you say it again?",
      ],
      [
        '您能说慢一点吗？',
        'nín néng shuō màn yì diǎn ma',
        'Could you speak a little more slowly?',
        'Worth more than any other sentence in the language early on.',
      ],
    ],
  },
  {
    id: 'isl_kanfa',
    title: 'Opinions and preferences',
    titleZh: '我的看法',
    useWhen: 'What turns an interview into a conversation.',
    tags: ['social', 'opinions'],
    hskLevel: 3,
    blocks: [
      ['我觉得这个主意不错。', 'wǒ jué de zhè ge zhǔ yi bú cuò', 'I think that is a good idea.'],
      ['这个我不太确定。', 'zhè ge wǒ bú tài què dìng', "I'm not too sure about that."],
      ['说实话，我不太喜欢。', 'shuō shí huà wǒ bú tài xǐ huan', "Honestly, I don't much like it."],
      ['那要看情况。', 'nà yào kàn qíng kuàng', 'That depends.', '看情况 — literally "look at the situation".'],
      ['我也是这么想的。', 'wǒ yě shì zhè me xiǎng de', 'That is what I think too.'],
    ],
  },
]
